import { z } from 'zod';
import { UI_LANGUAGE_CODES } from '../constants';
import { UserRole } from '../enums';
import { emailSchema, passwordSchema, phoneSchema, zEnum } from './common';

/**
 * Job seekers authenticate with a phone OTP (no password — most of them will
 * never set one). Employers and admins use email + password.
 */

/**
 * The role a NEW account should be created with. Omitted by the unified
 * sign-in screen on the first attempt, because at that point nobody knows yet
 * whether this person already has an account. An existing account's stored
 * role always wins, so this is only ever consulted at registration — the
 * client can never use it to change what an established user is.
 *
 * The enum is also the boundary that stops self-registration ever producing an
 * admin or staff account; those are created by a SUPER_ADMIN.
 */
const selfRegisterRole = z.enum([UserRole.SEEKER, UserRole.EMPLOYER]).optional();

/**
 * Sent back in the error envelope's `error` field when the identity itself is
 * good but belongs to no account yet, and none can be created without knowing
 * which kind to make. The sign-in screen watches for exactly this string and
 * asks the question, rather than treating it as a failure.
 */
export const ROLE_REQUIRED = 'ROLE_REQUIRED';

export const requestOtpSchema = z.object({
  phone: phoneSchema,
  role: selfRegisterRole,
});
export type RequestOtpInput = z.infer<typeof requestOtpSchema>;

export const verifyOtpSchema = z.object({
  phone: phoneSchema,
  code: z.string().trim().regex(/^\d{4,8}$/, 'Enter the OTP sent to your mobile'),
  role: selfRegisterRole,
  /** Optional name captured on first-time registration. */
  fullName: z.string().trim().min(2).max(120).optional(),
});
export type VerifyOtpInput = z.infer<typeof verifyOtpSchema>;

/**
 * Google sign-in. See selfRegisterRole: the role is only consulted when this
 * Google account is new to us, and an existing account's own role always wins.
 */
export const googleSignInSchema = z.object({
  idToken: z.string().min(1, 'Google sign-in failed. Please try again.'),
  role: selfRegisterRole,
});
export type GoogleSignInInput = z.infer<typeof googleSignInSchema>;

/**
 * Attaching a mobile to an account that already exists — used by seekers who
 * registered with Google and have no phone yet. Distinct from requestOtpSchema,
 * which resolves a user *from* a phone rather than attaching one to a session.
 */
export const requestPhoneOtpSchema = z.object({ phone: phoneSchema });
export type RequestPhoneOtpInput = z.infer<typeof requestPhoneOtpSchema>;

export const verifyPhoneSchema = z.object({
  phone: phoneSchema,
  code: z.string().trim().regex(/^\d{4,8}$/, 'Enter the OTP sent to your mobile'),
});
export type VerifyPhoneInput = z.infer<typeof verifyPhoneSchema>;

export const passwordLoginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, 'Password is required'),
});
export type PasswordLoginInput = z.infer<typeof passwordLoginSchema>;

export const employerSignupSchema = z
  .object({
    fullName: z.string().trim().min(2, 'Enter your full name').max(120),
    email: emailSchema,
    phone: phoneSchema,
    password: passwordSchema,
    confirmPassword: z.string(),
    companyName: z.string().trim().min(2, 'Enter the company name').max(200),
    designation: z.string().trim().max(100).optional(),
    acceptTerms: z.literal(true, {
      errorMap: () => ({ message: 'You must accept the terms to continue' }),
    }),
  })
  .refine((v) => v.password === v.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });
export type EmployerSignupInput = z.infer<typeof employerSignupSchema>;

export const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(1),
    newPassword: passwordSchema,
    confirmPassword: z.string(),
  })
  .refine((v) => v.newPassword === v.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });
export type ChangePasswordInput = z.infer<typeof changePasswordSchema>;

export const forgotPasswordSchema = z.object({ email: emailSchema });
export const resetPasswordSchema = z
  .object({
    token: z.string().min(10),
    newPassword: passwordSchema,
    confirmPassword: z.string(),
  })
  .refine((v) => v.newPassword === v.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

export const createStaffSchema = z.object({
  fullName: z.string().trim().min(2).max(120),
  email: emailSchema,
  phone: phoneSchema,
  password: passwordSchema,
  role: z.enum([UserRole.ADMIN, UserRole.STAFF]).default(UserRole.STAFF),
  permissions: z.array(z.string()).default([]),
});
export type CreateStaffInput = z.infer<typeof createStaffSchema>;

export const roleSchema = zEnum(UserRole);

/** Shape returned by POST /auth/* and GET /auth/me. */
export interface AuthUser {
  id: string;
  role: UserRole;
  fullName: string | null;
  phone: string | null;
  email: string | null;
  status: string;
  /** Present for EMPLOYER users. */
  companyId?: string | null;
  companyRole?: string | null;
  companyVerificationStatus?: string | null;
  /** Present for SEEKER users. */
  seekerId?: string | null;
  profileCompletion?: number;
  permissions: string[];
}

export interface AuthResponse {
  user: AuthUser;
  accessToken: string;
  /** Refresh token also lands in an httpOnly cookie; echoed for mobile clients. */
  refreshToken: string;
  isNewUser?: boolean;
}

/**
 * Interface language, validated against the shipped set rather than accepted as
 * a free string - an unknown code would silently fall back to English on every
 * later render with nothing to explain why.
 */
export const setLanguageSchema = z.object({
  language: z.enum(UI_LANGUAGE_CODES as unknown as [string, ...string[]]),
});
export type SetLanguageInput = z.infer<typeof setLanguageSchema>;
